import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CB, F, S, Section, T, optEmail, optInt, optNum, optStr, phone, optPhone } from "@/components/FormFields";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONDITIONS, FACING, FURNISHING, LISTING_TYPES, PROPERTY_STATUSES, PROPERTY_TYPES, SUBTYPES } from "@/lib/constants";
import { mediaUrl } from "@/lib/format";
import { PageHeader } from "@/pages/PlaceholderPage";

const schema = z.object({
  property_type: z.string().min(1, "Required"),
  property_subtype: optStr,
  listing_type: z.string().min(1, "Required"),
  condition_status: optStr,
  title: z.string().min(1, "Title is required"),
  status: z.string().min(1),
  entry_date: optStr,
  owner_name: z.string().min(1, "Owner name is required"),
  owner_address: optStr,
  owner_phone_primary: phone,
  owner_phone_secondary: optPhone,
  owner_email: z.preprocess((v) => (typeof v === "string" && v.trim() === "" ? undefined : v), z.string().email("Invalid email").optional()),
  owner_dob: optStr,
  owner_occupation: optStr,
  survey_no: optStr,
  address_line: z.string().min(1, "Address is required"),
  locality: z.string().min(1, "Locality is required"),
  city: z.string().min(1, "City is required"),
  unit_flat_no: optStr,
  floor_number: optStr,
  building_total_floors: optStr,
  saleable_area_sqft: optNum("Area must be a positive number"),
  plot_area_sqft: optNum("Area must be a positive number"),
  carpet_area_sqft: optNum("Area must be a positive number"),
  price_or_rent_amount: z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number({ required_error: "Price/Rent is required", invalid_type_error: "Price/Rent is required" }).positive("Must be a positive amount")
  ),
  price_or_rent_per_sqft: optNum(),
  deposit_amount: optNum(),
  maintenance_charge_monthly: optNum(),
  rent_escalation_percent: optNum(),
  price_note: optStr,
  bhk_count: optInt,
  bedrooms: optInt,
  bathrooms: optInt,
  furnishing_status: optStr,
  property_age_years: optInt,
  total_balconies: optInt,
  lift_available: z.boolean().optional(),
  height_ft: optNum(),
  frontage_ft: optNum(),
  power_supply: optStr,
  water_supply: optStr,
  parking_details: optStr,
  keys_with: optStr,
  facing_direction: optStr,
  distance_from_bus_stand: optStr,
  distance_from_railway_station: optStr,
  distance_from_market: optStr,
  amenities: z.preprocess(
    (v) => (typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v ?? []),
    z.array(z.string())
  ),
  reference_source: optStr,
  comments: optStr,
  declaration_signed: z.boolean().default(false),
});

const today = () => new Date().toISOString().slice(0, 10);

const blank = {
  property_type: "", property_subtype: "", listing_type: "", condition_status: "", title: "",
  status: "Available", entry_date: today(),
  owner_name: "", owner_address: "", owner_phone_primary: "", owner_phone_secondary: "", owner_email: "", owner_dob: "", owner_occupation: "",
  survey_no: "", address_line: "", locality: "", city: "Kolhapur", unit_flat_no: "", floor_number: "", building_total_floors: "",
  saleable_area_sqft: "", plot_area_sqft: "", carpet_area_sqft: "",
  price_or_rent_amount: "", price_or_rent_per_sqft: "", deposit_amount: "", maintenance_charge_monthly: "", rent_escalation_percent: "", price_note: "",
  bhk_count: "", bedrooms: "", bathrooms: "", furnishing_status: "", property_age_years: "", total_balconies: "",
  lift_available: false, height_ft: "", frontage_ft: "", power_supply: "", water_supply: "",
  parking_details: "", keys_with: "", facing_direction: "", distance_from_bus_stand: "", distance_from_railway_station: "", distance_from_market: "",
  amenities: "", reference_source: "", comments: "", declaration_signed: false,
};

const toFormValues = (p) => ({
  ...blank,
  ...Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v == null ? "" : v])),
  amenities: (p.amenities || []).join(", "),
  lift_available: !!p.lift_available,
  declaration_signed: !!p.declaration_signed,
});

export default function PropertyFormPage() {
  const { id } = useParams();
  const isEdit = !!id;

  const { data: property, isLoading } = useQuery({
    queryKey: ["property", id],
    queryFn: async () => (await api.get(`/properties/${id}`)).data,
    enabled: isEdit,
  });

  if (isEdit && (isLoading || !property)) {
    return <div className="p-8 text-slate-400">Loading…</div>;
  }
  return <PropertyForm key={id || "new"} isEdit={isEdit} id={id} property={property} />;
}

function PropertyForm({ isEdit, id, property }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState([]);
  const [serverError, setServerError] = useState("");

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: isEdit ? toFormValues(property) : blank,
  });
  const { handleSubmit, watch, setValue, formState: { isSubmitting } } = form;

  const propertyType = watch("property_type");
  const listingType = watch("listing_type");
  const isResidential = propertyType === "Residential";
  const isCommIndustrial = propertyType === "Commercial" || propertyType === "Industrial";
  const subtypeOptions = useMemo(() => SUBTYPES[propertyType] || [], [propertyType]);

  const onSubmit = async (data) => {
    setServerError("");
    const payload = { ...data };
    Object.keys(payload).forEach((k) => payload[k] === undefined && (payload[k] = null));
    if (isEdit) {
      payload.photo_urls = property?.photo_urls || [];
      payload.video_urls = property?.video_urls || [];
    }
    try {
      const prop = isEdit
        ? (await api.put(`/properties/${id}`, payload)).data
        : (await api.post("/properties", payload)).data;
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await api.post(`/properties/${prop.id}/media`, fd);
      }
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property", prop.id] });
      toast.success(isEdit ? "Property updated" : "Property added");
      navigate(`/properties/${prop.id}`);
    } catch (e) {
      setServerError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl" data-testid="property-form-page">
      <PageHeader title={isEdit ? "Edit Property" : "Add Property"} subtitle="Matches the paper Property Detail form" />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Section title="Property Info">
          <S form={form} prefix="prop" name="property_type" label="Property Type" options={PROPERTY_TYPES} required onValueChange={() => setValue("property_subtype", "")} />
          <S form={form} prefix="prop" name="property_subtype" label="Property Subtype" options={subtypeOptions} />
          <S form={form} prefix="prop" name="listing_type" label="Listing Type" options={LISTING_TYPES} required />
          <S form={form} prefix="prop" name="condition_status" label="Condition" options={CONDITIONS} />
          <F form={form} prefix="prop" name="title" label="Title (internal label)" required placeholder='e.g. "3BHK Rajarampuri – Owner Patil"' span="sm:col-span-2" />
          <S form={form} prefix="prop" name="status" label="Status" options={PROPERTY_STATUSES} />
          <F form={form} prefix="prop" name="entry_date" label="Entry Date" type="date" />
        </Section>

        <Section title="Owner Info">
          <F form={form} prefix="prop" name="owner_name" label="Owner Name" required />
          <F form={form} prefix="prop" name="owner_phone_primary" label="Phone (Primary)" required placeholder="10-digit mobile" />
          <F form={form} prefix="prop" name="owner_phone_secondary" label="Phone (Secondary)" />
          <F form={form} prefix="prop" name="owner_email" label="Email" type="email" />
          <F form={form} prefix="prop" name="owner_dob" label="Date of Birth" type="date" />
          <F form={form} prefix="prop" name="owner_occupation" label="Occupation" />
          <T form={form} prefix="prop" name="owner_address" label="Owner Address" />
        </Section>

        <Section title="Location">
          <F form={form} prefix="prop" name="survey_no" label="Survey No." />
          <F form={form} prefix="prop" name="address_line" label="Address Line" required span="sm:col-span-2" />
          <F form={form} prefix="prop" name="locality" label="Locality" required />
          <F form={form} prefix="prop" name="city" label="City" required />
          <F form={form} prefix="prop" name="unit_flat_no" label="Unit / Flat No." />
          <F form={form} prefix="prop" name="floor_number" label="Floor Number" placeholder='e.g. "3" or "Ground"' />
          <F form={form} prefix="prop" name="building_total_floors" label="Building Total Floors" placeholder='e.g. "7" or "G+7"' />
        </Section>

        <Section title="Dimensions (sqft)">
          <F form={form} prefix="prop" name="saleable_area_sqft" label="Saleable Area" type="number" />
          <F form={form} prefix="prop" name="plot_area_sqft" label="Plot Area" type="number" />
          <F form={form} prefix="prop" name="carpet_area_sqft" label="Carpet Area" type="number" />
        </Section>

        <Section title="Price">
          <F form={form} prefix="prop" name="price_or_rent_amount" label={listingType === "Rent" ? "Rent Amount (₹/month)" : "Price (₹)"} required type="number" />
          <F form={form} prefix="prop" name="price_or_rent_per_sqft" label="Price / Rent per sqft (₹)" type="number" />
          {listingType === "Rent" && <F form={form} prefix="prop" name="deposit_amount" label="Deposit Amount (₹)" type="number" />}
          <F form={form} prefix="prop" name="maintenance_charge_monthly" label="Maintenance / month (₹)" type="number" />
          {isCommIndustrial && <F form={form} prefix="prop" name="rent_escalation_percent" label="Rent Escalation (%)" type="number" />}
          <T form={form} prefix="prop" name="price_note" label="Price Note" />
        </Section>

        <Section title="Features">
          {isResidential && <F form={form} prefix="prop" name="bhk_count" label="BHK" type="number" />}
          <F form={form} prefix="prop" name="bedrooms" label="Bedrooms" type="number" />
          <F form={form} prefix="prop" name="bathrooms" label="Bathrooms" type="number" />
          <S form={form} prefix="prop" name="furnishing_status" label="Furnishing" options={FURNISHING} />
          <F form={form} prefix="prop" name="property_age_years" label="Property Age (years)" type="number" />
          {isResidential && <F form={form} prefix="prop" name="total_balconies" label="Total Balconies" type="number" />}
          <CB form={form} prefix="prop" name="lift_available" label="Lift Available" />
          {isCommIndustrial && <F form={form} prefix="prop" name="height_ft" label="Ceiling Height (ft)" type="number" />}
          {isCommIndustrial && <F form={form} prefix="prop" name="frontage_ft" label="Frontage (ft)" type="number" />}
          {isCommIndustrial && <F form={form} prefix="prop" name="power_supply" label="Power Supply" />}
          <F form={form} prefix="prop" name="water_supply" label="Water Supply" />
        </Section>

        <Section title="Additional Info">
          <F form={form} prefix="prop" name="parking_details" label="Parking Details" />
          <F form={form} prefix="prop" name="keys_with" label="Keys With" />
          <S form={form} prefix="prop" name="facing_direction" label="Facing Direction" options={FACING} />
          <F form={form} prefix="prop" name="distance_from_bus_stand" label="Distance from Bus Stand" />
          <F form={form} prefix="prop" name="distance_from_railway_station" label="Distance from Railway Station" />
          <F form={form} prefix="prop" name="distance_from_market" label="Distance from Market" />
          <F form={form} prefix="prop" name="amenities" label="Amenities (comma separated)" placeholder="Parking, 24x7 Security, Lift" span="sm:col-span-2" />
          <F form={form} prefix="prop" name="reference_source" label="Reference Source" />
          <T form={form} prefix="prop" name="comments" label="Comments" />
          <CB form={form} prefix="prop" name="declaration_signed" label="Declaration signed & brokerage agreed" />
        </Section>

        <Section title="Media (Photos & Videos)">
          <div className="sm:col-span-2 lg:col-span-3 space-y-3">
            {isEdit && (property?.photo_urls?.length || 0) + (property?.video_urls?.length || 0) > 0 && (
              <div className="flex flex-wrap gap-3">
                {property.photo_urls.map((u) => (
                  <img key={u} src={mediaUrl(u)} alt="Property" className="h-20 w-20 object-cover rounded-lg border border-slate-200" />
                ))}
                {property.video_urls.map((u) => (
                  <video key={u} src={mediaUrl(u)} className="h-20 w-28 rounded-lg border border-slate-200 bg-black" />
                ))}
              </div>
            )}
            <Input
              type="file"
              multiple
              accept="image/*,video/*"
              data-testid="prop-media-input"
              className="rounded-lg cursor-pointer"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
            {files.length > 0 && (
              <p className="text-sm text-slate-500">{files.length} file(s) selected: {files.map((f) => f.name).join(", ")}</p>
            )}
          </div>
        </Section>

        {serverError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="property-form-error">{serverError}</p>
        )}

        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="property-form-submit"
            className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white px-8 h-11 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md"
          >
            {isSubmitting ? "Saving…" : isEdit ? "Save Changes" : "Add Property"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-lg h-11">Cancel</Button>
        </div>
      </form>
    </div>
  );
}
